import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { savePendingAuthorizationQuery } from '@/lib/mcp-consent';
import { SessionRestoring, SessionUnavailable } from './SessionGate';

/**
 * Guarda da rota `/mcp/autorizar` (change platform-mcp-server, S-2.16a). Igual ao
 * `ProtectedRoute`, com uma diferença: sem sessão, guarda a query do pedido no `sessionStorage`
 * antes de mandar ao login, e o login volta ao caminho fixo do consentimento. O resto do app
 * segue no `ProtectedRoute`, sem nada disso.
 *
 * Enquanto a sessão é restaurada pelo cookie (`restoring`) ou a renovação falhou de passagem
 * (`unavailable`), a guarda espera sem gravar nem navegar: a query continua na URL, e a página,
 * com a sessão restaurada, faz a passagem dela para o `sessionStorage` como em qualquer chegada.
 *
 * Gravar durante a renderização é seguro aqui: a chave é fixa, o valor é o mesmo a cada render, e
 * a navegação do `<Navigate>` só acontece depois.
 */
export function McpAuthorizeRoute({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const sessionStatus = useAuthStore((s) => s.sessionStatus);
  const { search } = useLocation();
  if (accessToken) return <>{children}</>;
  if (sessionStatus === 'restoring') return <SessionRestoring />;
  if (sessionStatus === 'unavailable') return <SessionUnavailable />;
  savePendingAuthorizationQuery(search);
  return <Navigate to="/login" replace />;
}
